// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { View, Text, Checkbox, Link, Skeleton } from 'reshaped';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import {
  selectContracts,
  selectCartIsLoaded,
  selectCartIsFailure,
  selectCartIsWorking,
  approveContract,
  type Contract,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectIsPostingCheckout } from 'src/modules/app/selectors';
import { selectShopCurrent } from 'src/modules/organization/selectors';

import { ContractsPlaceholder } from './Contracts/ContractsPlaceholder';
import { ContractsButton } from './ContractsButton';

type RefType = HTMLDivElement;

function getBackgroundColor(
  isLoading: boolean,
  allContractsApproved: boolean,
): 'disabled-faded' | 'positive-faded' | 'warning-faded' {
  if (isLoading) return 'disabled-faded';
  return allContractsApproved ? 'positive-faded' : 'warning-faded';
}

function Contracts(
  props: {},
  ref: React.RefSetter<RefType | null>,
): null | React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const contracts = useAppSelector(selectContracts);

  const isLoaded = useAppSelector(selectCartIsLoaded);
  const isFailure = useAppSelector(selectCartIsFailure);

  const isAuthenticated = useAppSelector(selectIsAuthenticatedUser);
  const currentOrg = useAppSelector(state =>
    // $FlowFixMe[incompatible-call]: flow does't recognize multiple declarations of the function
    selectShopCurrent(state, isAuthenticated),
  );

  const isWorking = useAppSelector(selectCartIsWorking);

  const isCheckingContracts = useAppSelector(selectIsPostingCheckout);
  const isCartLoading = !isLoaded && (!isFailure || isWorking);
  const isOverviewPending = isCartLoading || isCheckingContracts;

  const acceptText = __('Please read and accept the following contracts:');
  const acceptedText = __('%(count)s contracts signed', {
    count: contracts.length,
  });

  const hasContracts = Boolean(Array.isArray(contracts) && contracts.length);
  const allContractsApproved =
    hasContracts && contracts.every(contract => contract.isApproved);

  const shouldDisplayVATWarning =
    currentOrg?.type === 'individual' && currentOrg?.country === 'FR';

  const willMount = React.useRef(true);

  React.useEffect(() => {
    if (!willMount.current) return;
    // Initialize contract approvals on first mount (client-side).
    contracts.forEach(contract => {
      dispatch(
        approveContract({
          identifier: contract.identifier,
          isApproved: false,
        }),
      );
    });
    willMount.current = false;
  }, [contracts, dispatch]);

  const raf2Ref = React.useRef<AnimationFrameID | null>(null);

  React.useEffect(() => {
    if (allContractsApproved) return undefined;

    const raf1 = requestAnimationFrame(() => {
      raf2Ref.current = requestAnimationFrame(() => {
        // $FlowFixMe[cannot-read]
        // $FlowFixMe[incompatible-use]
        // $FlowFixMe[prop-missing]
        ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2Ref.current) cancelAnimationFrame(raf2Ref.current);
    };
  }, [allContractsApproved, ref]);

  const handleContractCheckbox = ({ name, checked }: any) => {
    dispatch(approveContract({ identifier: name, isApproved: checked }));
  };

  const renderContract = (contract: Contract) => (
    <div key={contract.identifier}>
      <Checkbox
        name={contract.identifier}
        onChange={handleContractCheckbox}
        checked={contract.isApproved}
      >
        <Text as="p" variant="body-3">
          {contract.href ? (
            <Link
              href={contract.href}
              attributes={{
                target: '_blank',
                rel: 'noopener',
                title: __('%(name)s, (open in a new window)', {
                  name: contract.name,
                }),
              }}
            >
              {contract.name}
            </Link>
          ) : (
            contract.name
          )}
          {!!contract.registry_contract_href && (
            <Link
              key={`registry-contact-href-${contract.name}`}
              href={contract.registry_contract_href}
              attributes={{
                target: '_blank',
                rel: 'noopener',
                title: contract.href
                  ? __('%(label)s, open in a new window', {
                      label: __('and Registry contract'),
                    })
                  : undefined,
              }}
            >
              {' '}
              {__('and Registry contract')}
            </Link>
          )}
        </Text>
      </Checkbox>
    </div>
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <View
      as="div"
      padding={6}
      backgroundColor={getBackgroundColor(
        isOverviewPending,
        allContractsApproved,
      )}
      borderRadius="large"
    >
      <div ref={ref} style={{ scrollMarginBlockEnd: '152px' }}>
        <View direction="row" gap={2} justify="space-between" align="start">
          <View gap={1} grow>
            <Text as="h3" variant="body-2" weight="bold">
              {allContractsApproved
                ? __(
                    '<for-translators-use>%(count)s</for-translators-use>Contracts accepted',
                    { count: contracts.length },
                  )
                : __(
                    '<for-translators-use>%(count)s</for-translators-use>Contracts to accept',
                    { count: contracts.length },
                  )}
            </Text>
            {isOverviewPending ? (
              <Skeleton width="15rem" />
            ) : (
              hasContracts && (
                <Text as="p" variant="body-3">
                  {allContractsApproved ? acceptedText : acceptText}
                </Text>
              )
            )}
          </View>
          {!isOverviewPending && hasContracts && <ContractsButton />}
        </View>

        {isOverviewPending && (
          <ContractsPlaceholder
            shouldDisplayVATWarning={shouldDisplayVATWarning}
          />
        )}

        {!isOverviewPending && !allContractsApproved && (
          <View paddingTop={6}>
            {hasContracts ? (
              <View direction="column" gap={5} justify="space-between">
                <View direction="column" gap={4} divided>
                  {contracts.map(renderContract)}
                </View>

                {shouldDisplayVATWarning && (
                  <Text as="p" variant="body-3">
                    {__(
                      "Offering services delivered electronically to non-taxable persons whose permanent or usual residence is within the territory of the European Union is subject to VAT in the buyer's state of residence. The information provided in your user account, then, should match those of your permanent or usual residence.",
                    )}
                  </Text>
                )}
              </View>
            ) : (
              <div>{__('No contracts available.')}</div>
            )}
          </View>
        )}
      </div>
    </View>
  );
}

const ContractsForwarded: React.ComponentType<{}> = React.forwardRef<
  {},
  RefType | null,
>(Contracts);

export default ContractsForwarded;
